import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Flag, Loader2, ShieldCheck, ShieldAlert } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { formatDateNumeric } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

const STATUS = { open: ["Abierta", "bg-destructive text-white"], kept: ["Revisada · bloqueado", "bg-amber-500 text-white"], reinstated: ["Rehabilitado", "bg-emerald-600 text-white"] };

const Row = ({ r, onAct, busy }) => (
  <Card className="p-4 space-y-2" data-testid={`report-row-${r.id}`}>
    <div className="flex flex-wrap items-center gap-2">
      <Badge className={`${STATUS[r.status][1]} hover:opacity-90`}>{STATUS[r.status][0]}</Badge>
      <span className="font-semibold">{r.business_name}</span>
      <span className="text-xs text-muted-foreground">· {r.workshop_name} · {formatDateNumeric(r.created_at)}</span>
      {r.maestro_suspended && <span className="text-xs text-destructive inline-flex items-center gap-1 ml-auto"><ShieldAlert className="w-3.5 h-3.5" />Cuenta deshabilitada</span>}
    </div>
    <p className="text-sm"><strong>{r.category_title}</strong> › {r.reason_title}</p>
    <p className="text-sm text-muted-foreground whitespace-pre-wrap">{r.description}</p>
    <p className="text-xs text-muted-foreground">Denunciado por {r.reporter_name} ({r.reporter_email})</p>
    {r.resolution_note && <p className="text-xs">Nota: {r.resolution_note}</p>}
    {r.status !== "reinstated" && (
      <div className="flex flex-wrap gap-2 pt-1">
        <Button size="sm" disabled={busy} onClick={() => onAct(r, "reinstate")} className="gap-1.5" data-testid={`report-reinstate-${r.id}`}><ShieldCheck className="w-4 h-4" />Rehabilitar Maestro</Button>
        {r.status === "open" && <Button size="sm" variant="outline" disabled={busy} onClick={() => onAct(r, "keep")} data-testid={`report-keep-${r.id}`}>Mantener bloqueado</Button>}
      </div>
    )}
  </Card>
);

export const AdminReports = ({ onCount }) => {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/reports").then((r) => { setRows(r.data); onCount?.(r.data.filter((x) => x.status === "open").length); }).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  const act = async (r, action) => {
    const note = window.prompt(action === "reinstate" ? `¿Rehabilitar a ${r.business_name}? Sus talleres volverán a publicarse. Nota (opcional):` : "Nota de la revisión (opcional):", "");
    if (note === null) return;
    setBusy(true);
    try { await api.post(`/admin/reports/${r.id}/resolve`, { action, note }); toast.success(action === "reinstate" ? "Maestro rehabilitado" : "Denuncia revisada"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  if (rows === null) return <Loader2 className="w-6 h-6 animate-spin text-primary mx-auto mt-10" />;
  return (
    <div className="pt-6 space-y-3" data-testid="admin-reports">
      <p className="text-sm text-muted-foreground flex items-center gap-2"><Flag className="w-4 h-4 text-destructive" />Cada denuncia deshabilita al Maestro hasta que lo contactes y decidas rehabilitarlo.</p>
      {rows.length === 0 && <p className="text-sm text-muted-foreground py-8 text-center" data-testid="admin-reports-empty">No hay denuncias.</p>}
      {rows.map((r) => <Row key={r.id} r={r} onAct={act} busy={busy} />)}
    </div>
  );
};
